import React, { useState, useEffect } from 'react';
import './AIProcessingStatus.css';

const AIProcessingStatus = () => {
  const [aiConfig, setAiConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Load AI configuration on component mount
    loadAIConfig();
    // Re-load when something else changes engine availability
    // (e.g. the on-demand ESRGAN download in Settings)
    const refresh = () => loadAIConfig();
    window.addEventListener('ai-config-updated', refresh);
    return () => window.removeEventListener('ai-config-updated', refresh);
  }, []);

  const loadAIConfig = async () => {
    try {
      if (window.electronAPI && window.electronAPI.getAIConfig) {
        const config = await window.electronAPI.getAIConfig();
        setAiConfig(config);
      }
    } catch (error) {
      console.error('Failed to load AI config:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const hasLocalEngine = (config) =>
    !!(config && (config.localProcessingEnabled || config.seedvr2Available));

  const handleToggleProcessing = async () => {
    if (!hasLocalEngine(aiConfig)) return;

    try {
      const newPreference = !aiConfig.preferLocalProcessing;
      await window.electronAPI.setLocalProcessing(newPreference);

      // Reload config to reflect changes
      await loadAIConfig();
    } catch (error) {
      console.error('Failed to toggle processing mode:', error);
    }
  };

  if (isLoading || !aiConfig) {
    return null;
  }

  // Only show if a local engine (SeedVR2 or ESRGAN) is available
  if (!hasLocalEngine(aiConfig)) {
    return null;
  }

  // Label shows the active engine (set in Settings) when local is preferred
  const engineLabel = aiConfig.preferLocalProcessing
    ? (aiConfig.upscaleEngine === 'seedvr2' ? 'SeedVR2'
      : aiConfig.upscaleEngine === 'esrgan' ? 'ESRGAN'
      : 'Local')
    : 'Cloud';

  return (
    <div className="ai-processing-status">
      <div className="ai-status-indicator">
        <div className={`status-dot ${aiConfig.preferLocalProcessing ? 'local' : 'cloud'}`}></div>
        <span className="status-text">
          AI Upscale: {engineLabel}
        </span>
      </div>
      
      <button 
        className="processing-toggle"
        onClick={handleToggleProcessing}
        title={`Switch to ${aiConfig.preferLocalProcessing ? 'cloud' : 'local'} processing`}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M7 16V4M7 4L3 8M7 4L11 8M17 8V20M17 20L21 16M17 20L13 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      
      {aiConfig.gpuInfo && aiConfig.gpuInfo.hasGPU && (
        <div className="gpu-info" title={`GPU: ${aiConfig.gpuInfo.gpus[0].name}`}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      )}
    </div>
  );
};

export default AIProcessingStatus;